import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { GitMerge, Mail, Eye, Archive, Trash2, RotateCcw, UserMinus, FolderOpen, KeyRound, BadgeDollarSign, Pencil, UserPlus, Plus } from "lucide-react";
import { STAGES, STAGE_LABELS } from "../../components/shared";

const selectCls = "border border-neutral-300 rounded-md px-2 py-1.5 text-sm bg-white";
const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

export default function Users() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [merge, setMerge] = useState({ source: "", target: "" });
  const [removeTarget, setRemoveTarget] = useState(null);
  const [credTarget, setCredTarget] = useState(null);
  const [credForm, setCredForm] = useState({ email: "", new_password: "" });
  const [credBusy, setCredBusy] = useState(false);
  const [planTarget, setPlanTarget] = useState(null);
  const [planForm, setPlanForm] = useState({ plan_id: "", monthly_price: "" });
  const [plans, setPlans] = useState([]);
  const [planBusy, setPlanBusy] = useState(false);
  const [profTarget, setProfTarget] = useState(null);
  const [profForm, setProfForm] = useState({ name: "", legal_name: "", pen_names: "", bio: "", tax_info: "", payout_details: "" });
  const [profBusy, setProfBusy] = useState(false);
  const [booksTarget, setBooksTarget] = useState(null);
  const [userBooks, setUserBooks] = useState(null);
  const [newBook, setNewBook] = useState({ title: "", genre: "", status: "submitted" });
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "author" });
  const [addBusy, setAddBusy] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doMerge = async () => {
    if (!merge.source || !merge.target) return toast.error("Pick both accounts");
    const src = users.find((u) => u.id === merge.source);
    const tgt = users.find((u) => u.id === merge.target);
    if (!window.confirm(`Merge "${src?.name}" into "${tgt?.name}"?\n\nAll books, sales and payments of ${src?.name} will move to ${tgt?.name}, and the account "${src?.email}" will be deleted. This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/users/merge", { source_user_id: merge.source, target_user_id: merge.target });
      toast.success(`Merged: ${data.projects_moved} books and ${data.sales_rows_moved} sales rows moved to ${data.merged_into.name}`);
      setMerge({ source: "", target: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const sendInvite = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/invite`);
      await navigator.clipboard.writeText(`${window.location.origin}/claim?token=${data.claim_token}`);
      toast.success(`Claim link for ${u.name} copied to clipboard (valid 30 days)`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const impersonate = async (u) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${u.id}`);
      setUser(data);
      toast.success(`Now viewing as ${u.name}`);
      navigate("/dashboard");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeAuthor = async (mode) => {
    const u = removeTarget;
    if (mode === "purge" && !window.confirm(`PERMANENTLY delete ${u.name}, their books, sales, payouts and files?\n\nThis cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/remove`, { mode });
      toast.success(data.message);
      setRemoveTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/reactivate`);
      toast.success(`${u.name} reactivated`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openCred = (u) => { setCredTarget(u); setCredForm({ email: u.email, new_password: "" }); };

  const openBooks = (u) => {
    setBooksTarget(u);
    setUserBooks(null);
    setNewBook({ title: "", genre: "", status: "submitted" });
    api.get("/projects").then((r) => setUserBooks(r.data.filter((p) => p.author_id === u.id)));
  };

  const setBookStage = async (p, status) => {
    try {
      await api.put(`/admin/projects/${p.id}/status`, { status, internal_note: "Stage set from Users page" });
      toast.success(`"${p.title}" → ${STAGE_LABELS[status]} — author notified`);
      setUserBooks((bs) => bs.map((b) => (b.id === p.id ? { ...b, status } : b)));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addBook = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    try {
      const { data } = await api.post("/admin/projects", { author_id: booksTarget.id, ...newBook });
      toast.success(`"${data.title}" created — open it to upload the manuscript & cover`);
      navigate(`/projects/${data.id}`);
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setAddBusy(false); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    try {
      const payload = { ...newUser };
      if (!payload.password) delete payload.password;
      await api.post("/admin/users", payload);
      toast.success(`${newUser.name} created${newUser.password ? "" : " — use Invite to send them a claim link"}`);
      setShowAddUser(false);
      setNewUser({ name: "", email: "", password: "", role: "author" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setAddBusy(false); }
  };

  const openProf = (u) => {
    setProfTarget(u);
    setProfForm({ name: u.name || "", legal_name: u.legal_name || "", pen_names: (u.pen_names || []).join(", "),
      bio: u.bio || "", tax_info: u.tax_info || "", payout_details: u.payout_details || "" });
  };

  const saveProf = async (e) => {
    e.preventDefault();
    setProfBusy(true);
    try {
      await api.put(`/admin/users/${profTarget.id}/profile`, {
        ...profForm, pen_names: profForm.pen_names.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(`Profile updated for ${profForm.name || profTarget.name}`);
      setProfTarget(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setProfBusy(false); }
  };

  const openPlan = (u) => {
    setPlanTarget(u);
    const m = u.membership || {};
    setPlanForm({ plan_id: m.plan_id || "custom", monthly_price: m.custom_monthly_price || "" });
    if (plans.length === 0) api.get("/plans").then((r) => setPlans(r.data));
  };

  const savePlan = async (e) => {
    e.preventDefault();
    setPlanBusy(true);
    try {
      const payload = { plan_id: planForm.plan_id };
      if (planForm.monthly_price) payload.monthly_price = parseFloat(planForm.monthly_price);
      const { data } = await api.put(`/admin/users/${planTarget.id}/membership`, payload);
      toast.success(`${planTarget.name} is now on ${data.plan_name} at $${(data.custom_monthly_price || data.monthly_price).toFixed(2)}/mo`);
      setPlanTarget(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setPlanBusy(false); }
  };

  const saveCred = async (e) => {
    e.preventDefault();
    setCredBusy(true);
    try {
      const payload = {};
      if (credForm.email.trim() && credForm.email.trim() !== credTarget.email) payload.email = credForm.email.trim();
      if (credForm.new_password) payload.new_password = credForm.new_password;
      if (!Object.keys(payload).length) { toast.error("Change the email or enter a new password first"); setCredBusy(false); return; }
      await api.put(`/admin/users/${credTarget.id}/credentials`, payload);
      toast.success(`Login details updated for ${credTarget.name} — they've been notified`);
      setCredTarget(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setCredBusy(false); }
  };

  const authors = users.filter((u) => u.role === "author");

  return (
    <div data-testid="users-page">
      {removeTarget && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="remove-modal">
          <div className="bg-white rounded-lg border border-neutral-200 shadow-lg max-w-md w-full p-6">
            <h3 className="font-semibold font-heading text-lg text-neutral-900">Remove {removeTarget.name}?</h3>
            <p className="text-sm text-neutral-500 mt-2 mb-5">Choose what happens to their books, sales history and payout records.</p>
            <div className="space-y-3">
              <button data-testid="remove-archive-button" onClick={() => removeAuthor("archive")}
                className="w-full text-left border border-neutral-200 rounded-md p-4 hover:border-neutral-900 transition-colors">
                <p className="font-medium text-neutral-900 flex items-center gap-2"><Archive size={15} /> Archive — keep the data</p>
                <p className="text-xs text-neutral-500 mt-1">Account is deactivated (can't log in). Books, sales and payouts stay for your records. Reversible.</p>
              </button>
              <button data-testid="remove-purge-button" onClick={() => removeAuthor("purge")}
                className="w-full text-left border border-red-200 rounded-md p-4 hover:border-red-500 transition-colors">
                <p className="font-medium text-red-700 flex items-center gap-2"><Trash2 size={15} /> Delete everything</p>
                <p className="text-xs text-neutral-500 mt-1">Permanently deletes the account, books, manuscripts, covers, sales rows and payout records. Cannot be undone.</p>
              </button>
            </div>
            <button data-testid="remove-cancel-button" onClick={() => setRemoveTarget(null)}
              className="mt-4 w-full py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Cancel</button>
          </div>
        </div>
      )}
      {credTarget && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="credentials-modal">
          <form onSubmit={saveCred} className="bg-white rounded-lg border border-neutral-200 shadow-lg max-w-md w-full p-6">
            <h3 className="font-semibold font-heading text-lg text-neutral-900">Edit login — {credTarget.name}</h3>
            <p className="text-sm text-neutral-500 mt-1 mb-4">Change the account email and/or set a new password. The user is notified by email.</p>
            <label className="text-sm font-medium text-neutral-700">Login email</label>
            <input data-testid="cred-email-input" type="email" value={credForm.email}
              onChange={(e) => setCredForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">New password (leave blank to keep current)</label>
            <input data-testid="cred-password-input" type="text" minLength={8} placeholder="Min 8 characters" value={credForm.new_password}
              onChange={(e) => setCredForm((f) => ({ ...f, new_password: e.target.value }))} className={inputCls} />
            <div className="flex gap-3 mt-5">
              <button data-testid="cred-save-button" disabled={credBusy}
                className="flex-1 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {credBusy ? "Saving..." : "Save login details"}
              </button>
              <button type="button" data-testid="cred-cancel-button" onClick={() => setCredTarget(null)}
                className="px-5 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
      {planTarget && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="plan-modal">
          <form onSubmit={savePlan} className="bg-white rounded-lg border border-neutral-200 shadow-lg max-w-md w-full p-6">
            <h3 className="font-semibold font-heading text-lg text-neutral-900">Membership plan — {planTarget.name}</h3>
            <p className="text-sm text-neutral-500 mt-1 mb-4">
              Current: {planTarget.membership ? `${planTarget.membership.plan_name} at $${(planTarget.membership.custom_monthly_price || planTarget.membership.monthly_price)?.toFixed(2)}/mo` : "no membership"}. The author is notified by email.
            </p>
            <label className="text-sm font-medium text-neutral-700">Plan</label>
            <select data-testid="plan-select" value={planForm.plan_id}
              onChange={(e) => setPlanForm((f) => ({ ...f, plan_id: e.target.value }))} className={inputCls}>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}{p.custom ? " (negotiated rate)" : ` — $${p.monthly_price}/mo`}</option>)}
            </select>
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Agreed monthly rate ($ — blank = plan default)</label>
            <input data-testid="plan-rate-input" type="number" step="0.01" min="0.01" value={planForm.monthly_price}
              onChange={(e) => setPlanForm((f) => ({ ...f, monthly_price: e.target.value }))} className={inputCls}
              placeholder={String(plans.find((p) => p.id === planForm.plan_id)?.monthly_price ?? 100)} />
            <div className="flex gap-3 mt-5">
              <button data-testid="plan-save-button" disabled={planBusy}
                className="flex-1 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {planBusy ? "Saving..." : "Set plan & rate"}
              </button>
              <button type="button" data-testid="plan-cancel-button" onClick={() => setPlanTarget(null)}
                className="px-5 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
      {profTarget && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4 overflow-y-auto" data-testid="profile-modal">
          <form onSubmit={saveProf} className="bg-white rounded-lg border border-neutral-200 shadow-lg max-w-lg w-full p-6 my-8">
            <h3 className="font-semibold font-heading text-lg text-neutral-900">Edit profile — {profTarget.name}</h3>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div><label className="text-sm font-medium text-neutral-700">Display name</label>
                <input data-testid="prof-name-input" required value={profForm.name} onChange={(e) => setProfForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} /></div>
              <div><label className="text-sm font-medium text-neutral-700">Legal name</label>
                <input data-testid="prof-legal-input" value={profForm.legal_name} onChange={(e) => setProfForm((f) => ({ ...f, legal_name: e.target.value }))} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className="text-sm font-medium text-neutral-700">Pen names (comma-separated)</label>
                <input data-testid="prof-pen-input" value={profForm.pen_names} onChange={(e) => setProfForm((f) => ({ ...f, pen_names: e.target.value }))} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className="text-sm font-medium text-neutral-700">Bio</label>
                <textarea rows={3} value={profForm.bio} onChange={(e) => setProfForm((f) => ({ ...f, bio: e.target.value }))} className={inputCls} /></div>
              <div><label className="text-sm font-medium text-neutral-700">Tax info</label>
                <input value={profForm.tax_info} onChange={(e) => setProfForm((f) => ({ ...f, tax_info: e.target.value }))} className={inputCls} /></div>
              <div><label className="text-sm font-medium text-neutral-700">Payout details</label>
                <input value={profForm.payout_details} onChange={(e) => setProfForm((f) => ({ ...f, payout_details: e.target.value }))} className={inputCls} /></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button data-testid="prof-save-button" disabled={profBusy}
                className="flex-1 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {profBusy ? "Saving..." : "Save profile"}
              </button>
              <button type="button" data-testid="prof-cancel-button" onClick={() => setProfTarget(null)}
                className="px-5 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
      {booksTarget && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="user-books-modal"
          onClick={() => setBooksTarget(null)}>
          <div className="bg-white rounded-lg border border-neutral-200 shadow-lg max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold font-heading text-lg text-neutral-900 flex items-center gap-2"><FolderOpen size={17} /> Books — {booksTarget.name}</h3>
              <button data-testid="user-books-close" onClick={() => setBooksTarget(null)} className="text-sm text-neutral-400 hover:text-neutral-900">✕</button>
            </div>
            <p className="text-sm text-neutral-500 mb-4">{booksTarget.email}</p>
            {userBooks === null ? <p className="text-sm text-neutral-400">Loading...</p>
              : userBooks.length === 0 ? <p className="text-sm text-neutral-400" data-testid="user-books-empty">No books linked to this user yet.</p>
              : (
                <ul className="space-y-2">
                  {userBooks.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3">
                      <button data-testid={`user-book-${p.id}`} onClick={() => navigate(`/projects/${p.id}`)}
                        className="flex-1 min-w-0 text-left hover:underline" title="Open this book">
                        <p className="font-medium text-neutral-900 truncate">{p.title}</p>
                        <p className="text-xs text-neutral-400 mt-0.5">Updated {new Date(p.updated_at).toLocaleDateString()}</p>
                      </button>
                      <select data-testid={`user-book-stage-${p.id}`} value={p.status}
                        onChange={(e) => setBookStage(p, e.target.value)} title="Move this book through the pipeline"
                        className="border border-neutral-300 rounded-md px-2 py-1.5 text-xs bg-white">
                        {p.status === "draft" && <option value="draft" disabled>Draft (not submitted)</option>}
                        {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                      </select>
                    </li>
                  ))}
                </ul>
              )}
            <form onSubmit={addBook} className="mt-5 border-t border-neutral-100 pt-4">
              <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Plus size={14} /> Add a new manuscript</p>
              <div className="flex gap-2 flex-wrap">
                <input data-testid="new-book-title" required placeholder="Book title" value={newBook.title}
                  onChange={(e) => setNewBook((f) => ({ ...f, title: e.target.value }))}
                  className="flex-1 min-w-[160px] bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none" />
                <input data-testid="new-book-genre" placeholder="Genre" value={newBook.genre}
                  onChange={(e) => setNewBook((f) => ({ ...f, genre: e.target.value }))}
                  className="w-28 bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none" />
                <select data-testid="new-book-stage" value={newBook.status} title="Pipeline stage to start in"
                  onChange={(e) => setNewBook((f) => ({ ...f, status: e.target.value }))}
                  className="bg-white border border-neutral-300 rounded-md px-2 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none">
                  {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                </select>
                <button data-testid="new-book-save" disabled={addBusy}
                  className="px-4 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">Create</button>
              </div>
              <p className="text-xs text-neutral-400 mt-2">The book is placed straight into the selected pipeline stage and opens right away so you can upload the manuscript & cover.</p>
            </form>
          </div>
        </div>
      )}

      {showAddUser && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="add-user-modal">
          <form onSubmit={addUser} className="bg-white rounded-lg border border-neutral-200 shadow-lg max-w-md w-full p-6">
            <h3 className="font-semibold font-heading text-lg text-neutral-900">Add user</h3>
            <p className="text-sm text-neutral-500 mt-1 mb-4">Leave password blank to create an unclaimed account you can invite later.</p>
            <label className="text-sm font-medium text-neutral-700">Full name</label>
            <input data-testid="new-user-name" required value={newUser.name} onChange={(e) => setNewUser((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Email</label>
            <input data-testid="new-user-email" type="email" required value={newUser.email} onChange={(e) => setNewUser((f) => ({ ...f, email: e.target.value }))} className={inputCls} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Password (optional, min 8)</label>
            <input data-testid="new-user-password" type="text" minLength={newUser.password ? 8 : undefined} value={newUser.password}
              onChange={(e) => setNewUser((f) => ({ ...f, password: e.target.value }))} className={inputCls} placeholder="Blank = invite later" />
            {user?.role === "super_admin" && (
              <>
                <label className="text-sm font-medium text-neutral-700 mt-3 block">Role</label>
                <select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser((f) => ({ ...f, role: e.target.value }))} className={inputCls}>
                  <option value="author">Author</option>
                  <option value="admin">Admin</option>
                </select>
              </>
            )}
            <div className="flex gap-3 mt-5">
              <button data-testid="new-user-save" disabled={addBusy}
                className="flex-1 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {addBusy ? "Creating..." : "Create user"}
              </button>
              <button type="button" onClick={() => setShowAddUser(false)}
                className="px-5 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">Users</h1>
        <button data-testid="add-user-button" onClick={() => setShowAddUser(true)}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
          <UserPlus size={15} /> Add user
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-8" data-testid="merge-panel">
        <h3 className="font-semibold font-heading mb-1 flex items-center gap-2"><GitMerge size={17} /> Merge duplicate authors</h3>
        <p className="text-sm text-neutral-500 mb-4">Sales reports sometimes spell the same author differently. Merge moves all books, sales and payments into one account and deletes the duplicate.</p>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="merge-source-select" value={merge.source} onChange={(e) => setMerge((m) => ({ ...m, source: e.target.value }))} className={selectCls + " w-64 max-w-full"}>
            <option value="">Duplicate to remove...</option>
            {authors.filter((a) => a.id !== merge.target).map((a) => <option key={a.id} value={a.id}>{`${a.name} — ${a.email}`}</option>)}
          </select>
          <span className="text-sm text-neutral-400">merge into →</span>
          <select data-testid="merge-target-select" value={merge.target} onChange={(e) => setMerge((m) => ({ ...m, target: e.target.value }))} className={selectCls + " w-64 max-w-full"}>
            <option value="">Account to keep...</option>
            {authors.filter((a) => a.id !== merge.source).map((a) => <option key={a.id} value={a.id}>{`${a.name} — ${a.email}`}</option>)}
          </select>
          <button data-testid="merge-button" onClick={doMerge}
            className="px-4 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Merge accounts</button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
          <thead><tr className="bg-neutral-50">
            {["Name", "Email", "Role", "Joined", "Actions"].map((h) => (
              <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
          </tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-3 font-medium text-neutral-900">
                  <button data-testid={`user-books-link-${u.id}`} onClick={() => openBooks(u)}
                    className="hover:underline text-left font-medium text-neutral-900" title="View this user's books">
                    {u.name}
                  </button>
                  {u.auto_created && !u.disabled && <span className="ml-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">unclaimed</span>}
                  {u.disabled && <span className="ml-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">archived</span>}
                </td>
                <td className="px-6 py-3">{u.email}</td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                    u.role === "super_admin" ? "text-purple-700 bg-purple-50 border-purple-200" :
                    u.role === "admin" ? "text-blue-700 bg-blue-50 border-blue-200" :
                    "text-neutral-600 bg-neutral-50 border-neutral-200"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-6 py-3">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-6 py-3">
                  <div className="flex flex-wrap items-center gap-1.5 max-w-[430px]">
                    {user?.role === "super_admin" && (
                      <button data-testid={`edit-login-button-${u.id}`} onClick={() => openCred(u)}
                        title="Edit login email or reset password"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">
                        <KeyRound size={13} /> Login
                      </button>
                    )}
                    {user?.role === "super_admin" && (
                      <button data-testid={`edit-profile-button-${u.id}`} onClick={() => openProf(u)}
                        title="Edit user profile"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">
                        <Pencil size={13} /> Profile
                      </button>
                    )}
                    {user?.role === "super_admin" && u.role === "author" && (
                      <button data-testid={`edit-plan-button-${u.id}`} onClick={() => openPlan(u)}
                        title="Set membership plan & negotiated rate"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">
                        <BadgeDollarSign size={13} /> Plan
                      </button>
                    )}
                    {u.role === "author" && u.disabled && user?.role === "super_admin" && (
                      <button data-testid={`reactivate-button-${u.id}`} onClick={() => reactivate(u)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-md text-xs font-medium hover:bg-emerald-50">
                        <RotateCcw size={13} /> Reactivate
                      </button>
                    )}
                    {u.role === "author" && !u.disabled && (
                      <button data-testid={`docs-button-${u.id}`} onClick={() => navigate(`/admin/users/${u.id}/documents`)}
                        title="Manage documents & Library of Congress records"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">
                        <FolderOpen size={13} /> Docs
                      </button>
                    )}
                    {u.role === "author" && !u.disabled && (
                      <button data-testid={`invite-button-${u.id}`} onClick={() => sendInvite(u)}
                        title="Copy claim link (lets the author set their own email & password)"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">
                        <Mail size={13} /> Invite
                      </button>
                    )}
                    {u.role === "author" && !u.disabled && user?.role === "super_admin" && (
                      <button data-testid={`impersonate-button-${u.id}`} onClick={() => impersonate(u)}
                        title="View the platform exactly as this author sees it"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50">
                        <Eye size={13} /> Impersonate
                      </button>
                    )}
                    {u.role === "author" && user?.role === "super_admin" && (
                      <button data-testid={`remove-button-${u.id}`} onClick={() => setRemoveTarget(u)}
                        title="Remove this author from the platform"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-700 rounded-md text-xs font-medium hover:bg-red-50">
                        <UserMinus size={13} /> Remove
                      </button>
                    )}
                    {user?.role === "super_admin" && (
                      <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                        disabled={u.id === user.id} className={selectCls + " disabled:opacity-40"}>
                        {["author", "admin", "super_admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
